import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { combineLatest, iif, map, of, pipe, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { NgbActiveModal, NgbDatepickerModule, NgbModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { RentalAgreementValueDetailsModel } from '../rental-agreement-values.model';
import { ValueItemType } from '@app/core/models/common.model';
import { ReservationValuesService } from '@app/features/operations/reservations/reservation-values/reservation-values.service';
import { ReservationValueDetailsModel } from '@app/features/operations/reservations/reservation-values/reservation-values.model';
import { RaVersion } from '../../rental-agreements.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { newClientUid } from '@app/shared/utilities/client-uid.util';

interface RentalAgreementItemForm {
  itemType: FormControl<ValueItemType>;
  code: FormControl<string>;
  amountWithoutTax: FormControl<number | null>;
  quantity: FormControl<number | null>;
  taxRate: FormControl<number | null>;
  excess: FormControl<number | null>;
  isByRental: FormControl<boolean>;
  isIncluded: FormControl<boolean>;
  isSoldByBroker: FormControl<boolean>;
}

export type RentalAgreementValuesUpsertDialogComponentConfig =
  | { mode: 'create'; rentalAgreementUid: string; reservationNumber: string; daysNumber: number }
  | { mode: 'update'; item: RentalAgreementValueDetailsModel; reservationNumber: string }
  | { mode: 'readonly'; item: RentalAgreementValueDetailsModel; rentalAgreementVersion: RaVersion }
  | null;

@Component({
  standalone: true,
  selector: 'app-rental-agreement-values-upsert-dialog',
  templateUrl: './rental-agreement-values-upsert-dialog.component.html',
  imports: [ReactiveFormsModule, AsyncPipe, NgbNavModule, LoaderComponent, NgbDatepickerModule],
  host: { class: 'd-flex flex-column flex-fill' }
})
export class RentalAgreementValuesUpsertDialogComponent implements OnInit {
  private _formBuilder = inject(NonNullableFormBuilder);
  private _modal = inject(NgbActiveModal);
  private _reservationValuesService = inject(ReservationValuesService);
  private _modalService = inject(NgbModal);

  config = signal<RentalAgreementValuesUpsertDialogComponentConfig>(null);

  form: FormGroup<RentalAgreementItemForm>;
  item: RentalAgreementValueDetailsModel | null = null;
  errors: string[] | null = null;
  availableValues: ReservationValueDetailsModel[] = [];

  isReadonly = () => this.config()?.mode === 'readonly';

  constructor() {
    this.form = this._formBuilder.group<RentalAgreementItemForm>({
      itemType: this._formBuilder.control(0, [Validators.required]),
      code: this._formBuilder.control('', [Validators.required]),
      amountWithoutTax: this._formBuilder.control(null, [Validators.required]),
      quantity: this._formBuilder.control(null, [Validators.required]),
      taxRate: this._formBuilder.control(null, [Validators.required]),
      excess: this._formBuilder.control(null, [Validators.required]),
      isByRental: this._formBuilder.control(false, [Validators.required]),
      isIncluded: this._formBuilder.control(false, [Validators.required]),
      isSoldByBroker: this._formBuilder.control(false, [Validators.required])
    });

    this.form.controls.isSoldByBroker.disable();

    // Picking a code auto-fills the item's fields from the matching reservation extra.
    combineLatest([this.form.controls.code.valueChanges, toObservable(this.config)]).subscribe(([code, config]) => {
      const availableItem = this.availableValues.find((x) => x.code === code);
      if (!availableItem) return;

      this.form.controls.amountWithoutTax.enable();
      this.form.controls.amountWithoutTax.setValue(availableItem.amountWithoutTax);
      this.form.controls.taxRate.setValue(availableItem.taxRate);
      this.form.controls.excess.setValue(availableItem.excess);
      this.form.controls.itemType.setValue(availableItem.itemType);
      this.form.controls.isByRental.setValue(availableItem.isByRental);
      this.form.controls.isIncluded.setValue(availableItem.isIncluded);
      if (config?.mode === 'create') {
        this.form.controls.quantity.setValue(config.daysNumber);
      }
      if (code.toLowerCase() === 'dayvalue') {
        this.form.controls.amountWithoutTax.disable();
      }
    });
  }

  private _valueDetailsOperator = pipe(
    map(() => {
      const config = this.config();
      if (!config || config.mode === 'create') return null;
      return config.item;
    })
  );

  private _selectedValue$ = iif(
    () => this.config() !== null,
    of(true).pipe(
      this._valueDetailsOperator,
      map((value) => {
        this.item = value;
        this.initialize();
      })
    ),
    of(null)
  );

  vm$ = combineLatest([this._selectedValue$]).pipe(
    switchMap(() => {
      const config = this.config();
      if (config && (config.mode === 'create' || config.mode === 'update')) {
        return this._reservationValuesService.getDetailedList(config.reservationNumber);
      }
      return of([]);
    }),
    map((values: ReservationValueDetailsModel[]) => {
      this.availableValues = values;
      return of(null);
    })
  );

  ngOnInit(): void {
    this.form.markAllAsTouched();
  }

  submit() {
    this.errors = null;
    if (this.form.invalid) return;

    const config = this.config();
    if (config?.mode === 'update') {
      this.showDaysChangedWarning().subscribe(() => this._modal.close(this.createModelValues()));
    } else if (config?.mode === 'create') {
      this._modal.close({ ...this.createModelValues(), isSoldByBroker: false });
    }
  }

  dismiss() {
    this._modal.dismiss();
  }

  getItemTypeOptions() {
    return Object.values(ValueItemType)
      .filter((x) => !isNaN(x as number))
      .map((x) => ({ value: x, name: ValueItemType[x as number] }));
  }

  private initialize() {
    if (!this.item) return;

    this.form.setValue({
      itemType: this.item.itemType,
      code: this.item.code,
      amountWithoutTax: this.item.amountWithoutTax,
      quantity: this.item.quantity,
      taxRate: this.item.taxRate,
      excess: this.item.excess,
      isByRental: this.item.isByRental,
      isIncluded: this.item.isIncluded,
      isSoldByBroker: this.item.isSoldByBroker
    });

    this.form.markAsUntouched();

    // Broker-sold items are managed by the broker flow and can only be viewed here.
    if (this.item.isSoldByBroker) {
      this.config.set({ mode: 'readonly', item: this.item, rentalAgreementVersion: 'latest' });
    }

    if (this.config()?.mode === 'readonly') {
      this.form.disable();
    }

    if (this.config()?.mode === 'update') {
      this.form.enable();
      this.form.controls.itemType.disable();
      this.form.controls.code.disable();
      this.form.controls.isSoldByBroker.disable();
      if (this.item.code.toLowerCase() === 'dayvalue') {
        this.form.controls.amountWithoutTax.disable();
      }
    }
  }

  private createModelValues() {
    const formData = this.form.getRawValue();
    const config = this.config();
    return {
      ...formData,
      amountWithoutTax: formData.amountWithoutTax!,
      taxRate: formData.taxRate!,
      excess: formData.excess!,
      itemType: Number(formData.itemType),
      quantity: formData.quantity!,
      uid: config?.mode === 'create' ? newClientUid() : config?.item.uid
    };
  }

  private showDaysChangedWarning() {
    if (
      this.item &&
      this.item.code.toLowerCase() === 'dayvalue' &&
      this.form.controls.quantity.dirty &&
      this.form.controls.quantity.value !== this.item.quantity
    ) {
      const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
      modalRef.componentInstance.title = 'Number of days changed';
      modalRef.componentInstance.message =
        'Changing the number of days (quantity for the "DAYVALUE" item) will not update the drop-off date. Please update it manually if needed.';
      modalRef.componentInstance.confirmBtnText = 'Ok';
      modalRef.componentInstance.style = 'success';
      modalRef.componentInstance.icon = 'fa fa-warning text-warning';

      return modalRef.closed;
    }

    return of(null);
  }
}
